import { useEffect, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { ApiError, post } from '@/lib/api';
import { portalSlug, useReauth } from '@/lib/reauth';
import { useSession } from '@/stores/session';

const MESSAGES: Record<string, string> = {
  invalid_credentials: 'That did not match. Check it and try again.',
  too_many_attempts: 'Too many attempts. Wait a minute, then try again.',
  rate_limited: 'Too many attempts. Wait a minute, then try again.',
};

/**
 * Signing back in over the page whose request found the session gone (`lib/reauth.ts`). The
 * screen underneath stays mounted, draft and all; the request that was refused is sent again the
 * moment this succeeds. Mounted once, by `Root`.
 *
 * An account sign-in wears the public sheet; the console's wears the console, because the
 * console is where this happens most — a twelve-hour cookie running out at the counter.
 */
export default function ReauthDialog() {
  const kind = useReauth((state) => state.kind);
  const settle = useReauth((state) => state.settle);
  const username = useSession((state) => state.user?.username ?? '');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A fresh prompt every time it opens, with the account it was signed in as already filled in.
  useEffect(() => {
    if (!kind) return;
    setIdentifier(username);
    setPassword('');
    setError(null);
  }, [kind, username]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (kind === 'admin') {
        await post('/admin/login', { password, portal: portalSlug() });
      } else {
        await post('/auth/login', { identifier, password });
        await useSession.getState().load();
      }
      settle(true);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'request_failed';
      setError(MESSAGES[code] ?? 'Could not sign in. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  const admin = kind === 'admin';
  const feature = admin ? 'admin.reauth' : 'auth.reauth';

  return (
    <Dialog.Root open={kind !== null} onOpenChange={(open) => !open && settle(false)}>
      <Dialog.Portal>
        <Dialog.Overlay className={admin ? 'xa-scrim' : 'ee-scrim'} />
        <Dialog.Content className={admin ? 'xa xa-dialog' : 'ee-sheet'}>
          <form onSubmit={onSubmit}>
            <div className={admin ? 'xa-dialog__head' : 'ee-sheet__head'}>
              <Dialog.Title className={admin ? 'xa-card__title' : 'ee-sheet__title'}>
                Sign in again
              </Dialog.Title>
            </div>

            <div className={admin ? 'xa-dialog__body' : 'ee-sheet__body'}>
              <Dialog.Description asChild>
                <p className={admin ? 'xa-lead' : undefined}>
                  {admin
                    ? 'The console session ran out. Sign in and what you were doing carries on — nothing on this screen has been lost.'
                    : 'You were signed out. Sign in and what you were doing carries on — nothing on this screen has been lost.'}
                </p>
              </Dialog.Description>

              {admin ? null : (
                <Field label="Username, email or phone">
                  <Input
                    name="identifier"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    data-ee-feature={`${feature}.identifier`}
                  />
                </Field>
              )}

              <Field label="Password" error={error}>
                <PasswordInput
                  name="password"
                  autoComplete="current-password"
                  autoFocus
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  revealFeature={`${feature}.reveal`}
                  data-ee-feature={`${feature}.password`}
                />
              </Field>
            </div>

            <div className={admin ? 'xa-dialog__foot' : 'ee-sheet__foot'}>
              <Button
                type="submit"
                size="lg"
                variant={admin ? 'default' : 'solid'}
                disabled={busy}
                data-ee-feature={`${feature}.submit`}
              >
                {busy ? 'Signing in' : 'Sign in and carry on'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => settle(false)}
                data-ee-feature={`${feature}.cancel`}
              >
                Not now
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
